import React, { useMemo, useState } from "react";
import {
  Calendar,
  ChevronDown,
  FileDown,
  FileSpreadsheet,
  RotateCw,
  SlidersHorizontal,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import {
  applyPeriodChange,
  yearOptions,
} from "./financePeriodUtils";

export const DIRECTION_OPTIONS = [
  { value: "", label: "Tất cả" },
  { value: "in", label: "Thu" },
  { value: "out", label: "Chi" },
];

/** Phân nhóm theo nguồn hồ sơ + nhóm nghiệp vụ thường gặp */
export const GROUP_OPTIONS = [
  { value: "", label: "Tất cả nhóm" },
  { value: "receipt", label: "Phiếu thu" },
  { value: "advance", label: "Tạm ứng" },
  { value: "payment", label: "Thanh toán" },
  { value: "business_trip", label: "Công tác" },
  { value: "salary", label: "Lương" },
  { value: "purchase", label: "Mua hàng" },
  { value: "office", label: "Văn phòng / Hành chính" },
  { value: "training", label: "Đào tạo" },
];

const PERIOD_TYPES = [
  { value: "month", label: "Tháng" },
  { value: "quarter", label: "Quý" },
  { value: "year", label: "Năm" },
];

const selectCls =
  "appearance-none h-11 sm:h-9 w-full sm:w-auto sm:min-w-[7.5rem] rounded-xl sm:rounded-lg border-2 sm:border border-gray-200 bg-white pl-3 pr-8 text-sm font-semibold text-gray-800 outline-none transition hover:border-red-200 focus:border-red-400 focus:ring-2 focus:ring-red-100";

const advancedSelectCls =
  "w-full appearance-none rounded-xl border border-gray-200 bg-white px-3 py-2.5 pr-9 text-sm font-medium text-gray-700 outline-none transition focus:border-red-400 focus:ring-2 focus:ring-red-100";

function PeriodSelect({ value, onChange, children, ariaLabel, className = "" }) {
  return (
    <div className={`relative ${className}`}>
      <select
        value={value}
        onChange={onChange}
        aria-label={ariaLabel}
        className={selectCls}
      >
        {children}
      </select>
      <ChevronDown
        size={14}
        className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400"
        strokeWidth={2.5}
      />
    </div>
  );
}

export default function FinanceReportFilter({
  filters,
  onChange,
  onRefresh,
  onExportExcel,
  onExportPdf,
  exporting,
  canPickBranch,
  branchOptions = [],
  showPayerType = false,
  loading,
}) {
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const setPeriod = (patch) => onChange?.(applyPeriodChange(filters, patch));
  const update = (key, value) => onChange?.({ ...filters, [key]: value });

  const advancedCount = useMemo(() => {
    let n = 0;
    if (filters.branch_id) n += 1;
    if (filters.direction) n += 1;
    if (filters.group) n += 1;
    if (showPayerType && filters.payer_type) n += 1;
    return n;
  }, [filters, showPayerType]);

  const periodType = filters.period_type || "month";

  return (
    <div className="rounded-2xl border-2 sm:border border-gray-200 sm:border-gray-100 bg-white px-3 sm:px-4 py-3 shadow-sm space-y-3">
      {/* Hàng 1: kỳ thời gian */}
      <div>
        <div className="mb-2 inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-gray-500">
          <Calendar size={15} className="text-red-600" strokeWidth={2.5} />
          <span>Thời gian</span>
        </div>
        <div
          className="flex w-full rounded-xl border-2 border-gray-200 bg-gray-50 p-0.5"
          role="group"
          aria-label="Chọn loại kỳ báo cáo"
        >
          {PERIOD_TYPES.map((p) => {
            const active = periodType === p.value;
            return (
              <button
                key={p.value}
                type="button"
                onClick={() => setPeriod({ period_type: p.value })}
                className={`flex-1 rounded-lg px-2 py-2.5 sm:py-1.5 text-xs font-bold transition ${
                  active
                    ? "bg-red-600 text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
          {periodType === "month" && (
            <PeriodSelect
              value={filters.month || 1}
              onChange={(e) => setPeriod({ month: Number(e.target.value) })}
              ariaLabel="Chọn tháng"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  Tháng {m}
                </option>
              ))}
            </PeriodSelect>
          )}

          {periodType === "quarter" && (
            <PeriodSelect
              value={filters.quarter || 1}
              onChange={(e) => setPeriod({ quarter: Number(e.target.value) })}
              ariaLabel="Chọn quý"
            >
              {[1, 2, 3, 4].map((q) => (
                <option key={q} value={q}>
                  Quý {q}
                </option>
              ))}
            </PeriodSelect>
          )}

          <PeriodSelect
            value={filters.year || ""}
            onChange={(e) => setPeriod({ year: Number(e.target.value) })}
            ariaLabel="Chọn năm"
            className={periodType === "year" ? "col-span-2 sm:col-span-1 sm:min-w-[8rem]" : "sm:min-w-[8rem]"}
          >
            {yearOptions().map((y) => (
              <option key={y} value={y}>
                Năm {y}
              </option>
            ))}
          </PeriodSelect>
        </div>
      </div>

      {/* Hàng 2: thao tác */}
      <div className="flex items-center gap-2 border-t border-gray-100 pt-3">
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          title="Bộ lọc nâng cao"
          aria-label="Bộ lọc nâng cao"
          className={`relative inline-flex h-11 w-11 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-xl border-2 sm:border transition ${
            advancedOpen || advancedCount > 0
              ? "border-red-200 bg-red-50 text-red-700"
              : "border-gray-200 text-gray-500 hover:border-red-300 hover:text-red-600"
          }`}
        >
          <SlidersHorizontal size={16} />
          {advancedCount > 0 ? (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-700 px-1 text-[9px] font-bold text-white">
              {advancedCount}
            </span>
          ) : null}
        </button>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          title="Tải lại"
          aria-label="Tải lại"
          className="inline-flex h-11 w-11 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-xl border-2 sm:border border-gray-200 text-gray-500 transition hover:border-red-300 hover:text-red-600 disabled:opacity-60"
        >
          <RotateCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
        <div className="ml-auto flex flex-1 sm:flex-none items-center gap-2">
          <button
            type="button"
            onClick={onExportExcel}
            disabled={exporting || loading}
            className="inline-flex h-11 sm:h-9 flex-1 sm:flex-none items-center justify-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 text-xs font-bold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
          >
            <FileSpreadsheet size={14} />
            Excel
          </button>
          <button
            type="button"
            onClick={onExportPdf}
            disabled={exporting || loading}
            className="inline-flex h-11 sm:h-9 flex-1 sm:flex-none items-center justify-center gap-1.5 rounded-xl bg-red-700 px-3 text-xs font-bold text-white hover:bg-red-800 disabled:opacity-60"
          >
            <FileDown size={14} />
            PDF
          </button>
        </div>
      </div>

      {/* Hàng 2: nâng cao (mặc định ẩn) */}
      <AnimatePresence initial={false}>
        {advancedOpen && (
          <motion.div
            key="advanced-filters"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="mt-3 grid gap-3 border-t border-gray-100 pt-3 sm:grid-cols-2 lg:grid-cols-4">
              {canPickBranch && (
                <label className="block">
                  <span className="mb-1 block text-[10px] font-black uppercase tracking-wider text-gray-400">
                    Chi nhánh
                  </span>
                  <div className="relative">
                    <select
                      value={filters.branch_id || ""}
                      onChange={(e) => update("branch_id", e.target.value)}
                      className={advancedSelectCls}
                    >
                      <option value="">Tất cả chi nhánh</option>
                      {branchOptions.map((b) => (
                        <option key={b.value} value={b.value}>
                          {b.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      size={14}
                      className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                  </div>
                </label>
              )}

              <label className="block">
                <span className="mb-1 block text-[10px] font-black uppercase tracking-wider text-gray-400">
                  Phân loại
                </span>
                <div className="relative">
                  <select
                    value={filters.direction || ""}
                    onChange={(e) => update("direction", e.target.value)}
                    className={advancedSelectCls}
                  >
                    {DIRECTION_OPTIONS.map((o) => (
                      <option key={o.value || "all"} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                </div>
              </label>

              <label className="block">
                <span className="mb-1 block text-[10px] font-black uppercase tracking-wider text-gray-400">
                  Phân nhóm
                </span>
                <div className="relative">
                  <select
                    value={filters.group || ""}
                    onChange={(e) => update("group", e.target.value)}
                    className={advancedSelectCls}
                  >
                    {GROUP_OPTIONS.map((o) => (
                      <option key={o.value || "all"} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                </div>
              </label>

              {showPayerType && (
                <label className="block">
                  <span className="mb-1 block text-[10px] font-black uppercase tracking-wider text-gray-400">
                    Nguồn thu
                  </span>
                  <div className="relative">
                    <select
                      value={filters.payer_type || ""}
                      onChange={(e) => update("payer_type", e.target.value)}
                      className={advancedSelectCls}
                    >
                      <option value="">Tất cả nguồn</option>
                      <option value="student">Học viên</option>
                      <option value="school">Trường học</option>
                      <option value="customer">Doanh nghiệp</option>
                      <option value="other">Khác</option>
                    </select>
                    <ChevronDown
                      size={14}
                      className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                  </div>
                </label>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
